<template>
    <loading-indicator :text="textLoading" v-if="loading"/>
    <div v-else-if="confirmDelete">
        Sind Sie sicher, dass Sie den Web-Export löschen möchten?
    </div>
    <div v-else-if="settings.exists">
        <div class="hrline-text">
            <span>Skript</span>
        </div>
        <div class="form-row half-margin">
            Der Export-Token wurde generiert, bitte fügen Sie das folgende Skript auf der Seite ein, wo der Web-Export angezeigt werden soll.

            <span v-if="settings.two_click_enabled">
                <strong>Hinweis:</strong> Die Zwei-Klick-Lösung ist aktiviert. Einstellungen und weitere Informationen dazu finden Sie <router-link
                :to="{name: 'settings-export'}">hier</router-link>.
            </span>
        </div>

        <app-code v-if="settings.two_click_enabled">
            &lt;div id=&quot;{{settings.token}}&quot; onclick=&quot;loadExport(true)&quot; style=&quot;cursor:pointer;padding:30px 20px;min-height:30vh;text-align:center;font-size:1.1rem;color:#4f4f4f;background:#e3e3e3;&quot;&gt;&lt;a onclick=&quot;loadExport(true)&quot; title=&quot;Inhalte anzeigen&quot;&gt;Klicken Sie hier, um die Inhalte von &quot;{{settings.server}}&quot; anzeigen zu lassen&lt;/a&gt;&lt;br/&gt;&lt;small&gt;Beim Aufruf gelten abweichende Datenschutzbestimmungen der Webseite &quot;{{settings.server}}&quot;&lt;/small&gt;&lt;/div&gt;<br/>
            &lt;script type=&quot;text/javascript&quot;&gt;(function(d,c,b,f){function e(a){a&amp;&amp;(a=new Date,a.setTime(a.getTime()+864E5),c.cookie=&quot;export_&quot;+b.id+&quot;=1; path=/; expires=&quot;+a.toUTCString()+&quot;;&quot;);b.removeAttribute(&quot;style&quot;);b.innerHTML=&quot;&quot;;b.onclick=null;a=c.createElement(&quot;script&quot;);a.setAttribute(&quot;crossorigin&quot;,&quot;use-credentials&quot;);a.onload=function(){d.initializeExport(b.id)};a.src=f;a.type=&quot;text/javascript&quot;;b.parentNode.insertBefore(a,b.nextSibling)}-1!==c.cookie.indexOf(&quot;export_&quot;+b.id)&amp;&amp;e();d.loadExport=e})(window,document,document.getElementById(&quot;{{settings.token}}&quot;),&quot;{{settings.protocol}}://{{settings.server}}/export/js/initialize.js&quot;);&lt;/script&gt;
        </app-code>
        <app-code v-else>
            &lt;div id="{{settings.token}}"&gt;&amp;#160;&lt;/div&gt;<br>
            &lt;script type="text/javascript" src="{{settings.protocol}}://{{settings.server}}/export/js/initialize.js" crossorigin="use-credentials"&gt;&lt;/script&gt;<br>
            &lt;script type="text/javascript"&gt;initializeExport("{{settings.token}}")&lt;/script&gt;
        </app-code>

        <div class="hrline-text">
            <span>Einstellungen</span>
        </div>
        <div>
            <form-row v-show="withDescription" label="Name des Exports" label-for="settings-own-headline" class="half-margin">
                <form-input v-model="settings.description"/>
            </form-row>

            <form-row label="Überschrift anzeigen" label-for="settings-description" class="form-row-checkbox label-auto-width font-weight-500 half-margin">
                <form-input-checkbox v-model="settings.headline" class="toggle" :value-on="true" :value-off="false"/>
            </form-row>
            <div class="form-row half-margin">
                Mit aktivierter Einstellung wird die Export-Überschrift verwendet. Alternativ können Sie auch eine individuelle Überschrift festlegen.
            </div>
            <form-row label="Überschrift" label-for="settings-own-headline" v-if="settings.headline" class="half-margin"
                help="Wenn Sie das Feld leer lassen, wird die gleiche Überschrift wie im Intranet verwendet.">
                <form-input v-model="settings.own_headline"/>
            </form-row>
            <form-row label="Vollbildmodus erlauben" label-for="settings-fullscreen" class="form-row-checkbox label-auto-width font-weight-500 half-margin" hide-validator-icon>
                <form-input-checkbox v-model="settings.fullscreen" :value-on="true" :value-off="false"/>
            </form-row>
            <div>
                Der Vollbildmodus ist gerade für Besucher mit kleineren Bildschirmgrößen vorteilhaft. Mit aktivierter Einstellung kann der Export Bildschirmfüllend angezeigt werden.
            </div>

            <portuna-box class="disclaimer spacer-15">
                Weitere Einstellungen zum Export finden Sie <a href="/settings/export">hier</a>.
            </portuna-box>
        </div>
    </div>
    <div v-else>
        {{defaultTextRequest}}
    </div>
</template>

<script setup lang="ts">
    import {usePortuna} from '@/composables/portuna';
    import axios from 'axios';
    import type {TinyEmitter} from 'tiny-emitter';
    import {reactive, ref, inject} from 'vue';
    import {popupDefaultCloseButton} from '@/library/popup';
    import TopMessageHandler from '../library/topMessageHandler';
    import {errorHandler} from '@/utils/portuna';
    import AppCode from './AppCode.vue';
    import FormInput from './form/FormInput.vue';
    import FormInputCheckbox from './form/FormInputCheckbox.vue';
    import FormRow from './form/FormRow.vue';
    import LoadingIndicator from './LoadingIndicator.vue';
    import PortunaBox from './PortunaBox.vue';

    const portuna = usePortuna();
    defineExpose(portuna);

    const popupEmitter = inject<TinyEmitter>('popup-emitter')!;

    const emit = defineEmits<{
        setData: [...args: unknown[]];
        onGenerated: [...args: unknown[]];
        onUpdated: [...args: unknown[]];
        onDeleted: [...args: unknown[]];
    }>();

    const props = withDefaults(
        defineProps<{
            exportData: ExportData;
            askBeforeCreate?: boolean;
            withDescription?: boolean;
        }>(),
        {
            askBeforeCreate: true,
            withDescription: false,
        },
    );

    const confirmDelete      = ref<boolean>(false);
    let settings             = reactive<Partial<ExportSettings>>({});
    const loading            = ref<boolean>(true);
    const textLoading        = ref<string>('Die Einstellungen werden abgefragt.');
    const defaultTextRequest = ref<string>(
        'Sie sind im Begriff einen Web-Export zu erstellen. Wollen Sie fortfahren?',
    );

    // close & reset
    const close = () =>
    {
        popupEmitter.emit('close');
        popupEmitter.emit('setData', {
            _textIcon: '',
            _textIconClass: 'text-grey',
        });
        popupEmitter.off('onGenerated');
        popupEmitter.off('onUpdated');
        popupEmitter.off('onDeleted');
    };

    loading.value     = true;
    textLoading.value = 'Die Einstellungen werden abgefragt.';

    axios.post('/export/check_token.json', props.exportData)
    .then(({data: {data}}) =>
    {
        settings      = data;
        loading.value = false;
        // no token exists -> create one
        if (!data.exists)
        {
            // if it is set to ask before create -> show confirm popup step
            if (props.askBeforeCreate)
            {
                popupEmitter.emit('setData', {
                    _textIcon: 'language',
                    _buttons: [
                        popupDefaultCloseButton,
                        {
                            type: 'primary',
                            title: 'Export erstellen',
                            icon: 'check',
                            cmd: () =>
                            {
                                createToken();
                            },
                        },
                    ],
                });
            }
            // otherwise just go to create
            else
            {
                createToken();
            }
        }
        // token exists - change buttons and show data [v-if="settings.exists" in template]
        else
        {
            setDefaultOptions();
        }
    })
    .catch(errorHandler);

    const createToken = () =>
    {
        loading.value     = true;
        textLoading.value = 'Der Export wird erstellt ...';

        popupEmitter.emit('setButtons', []);

        axios.post('/export/request_token.json', props.exportData)
        .then(({data: {data}}) =>
        {
            settings      = data;
            loading.value = false;
            // token not created -> show retry
            if (!data.exists)
            {
                defaultTextRequest.value = data.message;
                popupEmitter.emit('setData', {
                    _textIcon: 'language',
                    _buttons: [
                        popupDefaultCloseButton,
                        {
                            type: 'primary',
                            title: 'Erneut versuchen',
                            icon: 'check',
                            cmd: () =>
                            {
                                createToken();
                            },
                        },
                    ],
                });
            }
            else
            {
                popupEmitter.emit('onGenerated', data);
                setDefaultOptions();
            }
        })
        .catch(errorHandler);
    };

    const saveToken = () =>
    {
        loading.value     = true;
        textLoading.value = 'Der Export wird gespeichert ...';

        popupEmitter.emit('setButtons', []);

        axios.post('/export/configure_token_' + settings.token + '.json', settings)
        .then((response) =>
        {
            loading.value = false;
            setDefaultOptions();
            if ('OK' === response.data.status)
            {
                popupEmitter.emit('onUpdated', settings);
                TopMessageHandler.success('Die Einstellungen wurden erfolgreich gespeichert.');
            }
            else
            {
                TopMessageHandler.error(
                    (response.data && response.data.message) ||
                    'Die Einstellungen konnten nicht gespeichert werden.',
                );
            }
        })
        .catch(errorHandler)
        .finally(close);
    };

    const deleteToken = () =>
    {
        loading.value     = true;
        textLoading.value = 'Der Export wird gelöscht ...';

        popupEmitter.emit('setButtons', []);

        axios.post('/export/delete_token_' + settings.token + '.json', {
            resource: props.exportData.resource,
        })
        .then((response) =>
        {
            loading.value = false;
            if ('OK' === response.data.status)
            {
                popupEmitter.emit('onDeleted', settings);
                close();
                TopMessageHandler.success('Der Export wurde erfolgreich gelöscht.');
            }
            else
            {
                setDefaultOptions();
                TopMessageHandler.error(
                    (response.data && response.data.message) || 'Der Export konnte nicht gelöscht werden.',
                );
            }
        })
        .catch(errorHandler);
    };

    const askDeleteToken = () =>
    {
        confirmDelete.value = true;
        popupEmitter.emit('setData', {
            _textIcon: 'delete',
            _textIconClass: 'text-danger',
            _buttons: [
                {
                    type: 'default',
                    title: 'Abbrechen',
                    icon: 'clear',
                    cmd: setDefaultOptions,
                },
                {
                    type: 'danger',
                    title: 'Löschen',
                    icon: 'delete',
                    cmd: deleteToken,
                },
            ],
        });
    };

    const setDefaultOptions = () =>
    {
        confirmDelete.value = false;

        popupEmitter.emit('setData', {
            _textIcon: '',
            _textIconClass: 'text-grey',
            _buttons: [
                popupDefaultCloseButton,
                {
                    type: 'danger',
                    title: 'Export löschen',
                    icon: 'delete',
                    cmd: () =>
                    {
                        askDeleteToken();
                    },
                },
                {
                    type: 'primary',
                    title: 'Einstellungen speichern',
                    icon: 'check',
                    cmd: () =>
                    {
                        saveToken();
                    },
                },
            ],
        });
    };
</script>